import { Chip, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * The nightly default, kept EXACTLY as this chip behaved before the prop existed.
 *
 * ⚠️ 27 hours, not 26, and deliberately so. The original compared whole hours —
 * `diff(ingested, 'hour') > 26` — and that truncates, so it only turned amber at a full 27.
 * Writing 26 * 60 here reads more natural and would fire up to 59 minutes earlier, quietly
 * changing a page that already ships. Tighten it as its own decision, not as a side effect of
 * adding a dispatch board.
 */
const NIGHTLY_STALE_AFTER_MINUTES = 27 * 60;

/**
 * The honest staleness display (TRD §9.4 / decision #23): every analytics surface shows WHEN
 * its data was ingested, plainly — this chip IS the platform's freshness alerting.
 *
 * ⚠️ The threshold has to match the SURFACE'S cadence, not the platform's. A nightly report is
 * fine at 25 hours old; a dispatch board refreshed every 2 minutes is badly wrong at 25
 * MINUTES. Left at the nightly default, a frozen board shows its staleness in calm grey and
 * the warning never fires — which is the one failure a wall-facing screen cannot afford.
 */
export const AsOfChip = ({
  asOf,
  staleAfterMinutes = NIGHTLY_STALE_AFTER_MINUTES,
  zone,
  zoneLabel = '',
}: {
  asOf: string | null;
  /** Minutes before the chip turns amber. Set it from the surface's own refresh cadence. */
  staleAfterMinutes?: number;
  /**
   * Render the time in THIS zone rather than the viewer's own.
   *
   * ⚠️ Left undefined, the chip behaves exactly as it always has. The fresh produce pages
   * depend on that, and a component three screens share is the wrong place to change all
   * three at once. The dispatch board passes Pacific/Fiji because a wall display in Suva and
   * a laptop in Manila have to read the same clock — and because every other time on that
   * page is pinned to Fiji already, so an unpinned one here would be the odd reading out.
   */
  zone?: string;
  /** Short suffix naming the zone, e.g. FJT. Only meaningful alongside `zone`. */
  zoneLabel?: string;
}) => {
  if (!asOf) {
    return <Chip size="small" color="warning" label="No data yet" />;
  }
  const parsed = dayjs(asOf);
  // Staleness compares two instants, so it gives the same answer in every zone — pinning the
  // display must never move the amber threshold.
  const stale = dayjs().diff(parsed, 'minute') > staleAfterMinutes;
  const ingested = zone ? parsed.tz(zone) : parsed;
  const suffix = zone && zoneLabel ? ` ${zoneLabel}` : '';
  return (
    <Tooltip title={`${ingested.format('DD MMM YYYY, HH:mm')}${suffix}`}>
      <Chip
        size="small"
        color={stale ? 'warning' : 'default'}
        variant={stale ? 'filled' : 'outlined'}
        label={`Data as of ${ingested.format('DD MMM, HH:mm')}${suffix}${
          stale ? ' — sync overdue' : ''
        }`}
      />
    </Tooltip>
  );
};
